import React, { useContext, useEffect, useState } from 'react';
import { Table, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EnergyConsumption, EnergyDataRow } from '../../../../../../services/types/Calculator';
import { DropDown, Button, ConfirmModal, Modal } from '../../../../../../shared/components';
import MainContext from '../../../../../App/MainProvider/context';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import EnergyConsumptionDetails from './Details/EnergyConsumptionDetails';
import { calculateEnergyConsumptionData } from '../../Utils/moduleCalculates';
import EnergyConsumptionElectricity from './EnergyConsumptionElectricity';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

interface Props {
  energyConsumption: EnergyConsumption;
  onUpdateEnergyConsumption: (updatedEnergyConsumption: EnergyConsumption) => void;
}
const EnergyConsumptionSection: React.FC<Props> = ({
  energyConsumption,
  onUpdateEnergyConsumption,
}) => {
  const { t } = useTranslation();

  const energyConsumptionOptions = [
    { id: 0, text: t('Select'), value: '' },
    ...(energyConsumption.energyTypes
      ? energyConsumption.energyTypes.map((x) => ({
          id: x.id,
          text: x.name,
          value: x.id.toString(),
        }))
      : []),
  ];
  const litersPerGallong = 3.78541178;
  const [energy, setEnergy] = useState<EnergyConsumption>(energyConsumption);
  const { notify } = useContext(MainContext);
  const [selectedValueEnergyConsumptionOption, setSelectedValue] = useState<number | null>(0);
  const [energyInputValue, setEnergyInputValue] = useState<string>('');
  const [rows, setRows] = useState<EnergyDataRow[]>(energyConsumption.rows || []);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [rowForDelete, setRowForDelete] = useState<number | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSubTypeModal, setShowSubTypeModal] = useState(false);
  const [showSubModuleButton, setShowSubModuleButton] = useState(false);
  const [enableAddElectricity, setEnableAddElectricity] = useState(false);

  const isAddButtonDisabled =
    selectedValueEnergyConsumptionOption === null ||
    selectedValueEnergyConsumptionOption === 0 ||
    Number(energyInputValue) <= 0;

  const handleChange = (value: number) => {
    setSelectedValue(value);

    if (value === 0) {
      setShowSubModuleButton(false);
    }

    const selectedCoverType = energyConsumption.energyTypes.find((x) => x.id === value);

    if (selectedCoverType) {
      setShowSubModuleButton(selectedCoverType.hasSubTypes);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return; // Si excede 10 caracteres, no actualiza el estado
    }

    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setEnergyInputValue(value);
    }
  };

  const isDuplicateCoverType = (existingRows: EnergyDataRow[], coverId: number) => {
    const isDuplicate = existingRows.some((row) => row.energyTypeId === coverId);

    setEnableAddElectricity(false);
    if (isDuplicate) {
      notify(
        'Energy Consumption',
        'A row with this Energy Type already exists.',
        'danger',
        'exclamation-triangle'
      );
    }

    return isDuplicate;
  };

  const createAndAddRow = (value: number) => {
    const selectedCoverType = energyConsumption.energyTypes.find(
      (x) => x.id === selectedValueEnergyConsumptionOption
    );

    if (selectedCoverType) {
      if (isDuplicateCoverType(rows, selectedCoverType.id)) {
        return;
      }

      const newRow: EnergyDataRow = {
        id: 0,
        energyTypeId: selectedCoverType.id,
        energyType: selectedCoverType.name,
        energyUnit: selectedCoverType.energyUnit,
        value,
        heatingUnit: selectedCoverType.heatingUnit,
        heatingValue: selectedCoverType.heatingFactor,
        emissionUnit: selectedCoverType.emissionUnit,
        emissionFactor: selectedCoverType.emissionFactor,
        co2Factor: 0,
        co2Emission: 0,
        ch4FactorUnit: selectedCoverType.cH4Unit,
        ch4Factor: selectedCoverType.cH4Factor,
        ch4Emission: null,
        n2OFactorUnit: selectedCoverType.n20Unit,
        n2OFactor: selectedCoverType.n20Factor,
        n2OEmission: null,
        factorByCountry: selectedCoverType.factorByCountry,
        totalEmission: 0,
        subTypeRows: [],
        ch4Value: selectedCoverType.heatingFactor
          ? (value * selectedCoverType.heatingFactor) / litersPerGallong
          : value,
      };

      const newRowCalculated = calculateEnergyConsumptionData(newRow);

      if (showSubModuleButton) {
        newRowCalculated.subTypeRows = energy.eletricityRows;
      }

      setRows((prevRows) => {
        const updatedRows = [...prevRows, newRowCalculated];

        const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);
        onUpdateEnergyConsumption({
          ...energyConsumption,
          rows: updatedRows,
          subTotal: totalCarbonSum,
        });
        return updatedRows;
      });

      setShowSubModuleButton(false);
      setEnergyInputValue('');
      setSelectedValue(0);
    }
  };

  const handleSubAddRow = () => {
    if (
      energy.eletricityRowsTotal &&
      energy.eletricityRowsTotal !== null &&
      energy.eletricityRowsTotal > 0
    ) {
      createAndAddRow(energy.eletricityRowsTotal);
      onUpdateEnergyConsumption({
        ...energyConsumption,
        eletricityRows: energy.eletricityRows,
      });
    }
  };

  const handleAddRow = () => {
    if (selectedValueEnergyConsumptionOption !== null && Number(energyInputValue) > 0) {
      createAndAddRow(Number(energyInputValue));
    }
  };

  const confirmRemoveRow = () => {
    if (rowForDelete !== null) {
      setRows((prevRows) => {
        const updatedRows = prevRows.filter((_, i) => i !== rowForDelete);
        const totalCarbonSum = updatedRows.reduce((sum, row) => sum + row.totalEmission, 0);

        onUpdateEnergyConsumption({
          ...energyConsumption,
          rows: updatedRows.length > 0 ? updatedRows : [],
          subTotal: updatedRows.length > 0 ? totalCarbonSum : null,
        });
        return updatedRows;
      });
      setShowConfirmDelete(false);
      setRowForDelete(null);
    }
  };

  const handleRemoveRow = (index: number) => {
    setRowForDelete(index);
    setShowConfirmDelete(true);
  };

  const totalCarbonSum = rows.reduce((sum, row) => sum + row.totalEmission, 0);

  useEffect(() => {
    if (energyConsumption && energy.eletricityRowsTotal) {
      setEnableAddElectricity(true);
    } else {
      setEnableAddElectricity(false);
    }
  }, [energyConsumption, energyConsumption.eletricityRows, energy.eletricityRowsTotal]);

  return (
    <>
      <h4 className="table-calculator-title">{t('Select Parameters')}</h4>
      <div className="form-row align-items-center calculator-forms table-wrapper-calc">
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Energy Type')}
          </label>
        </div>
        <div className="col-sm-3 d-flex align-items-center">
          <DropDown
            elements={energyConsumptionOptions}
            value={selectedValueEnergyConsumptionOption}
            onChange={handleChange}
            key="drop-energy"
            className="dropdown-module-calculator"
          />
        </div>
        <div className="col-sm-2 d-flex align-items-center">
          <label className="col-form-label mr-2" htmlFor="sector">
            {t('Quantity Used')}
          </label>
        </div>

        <div className="col-sm-2 d-flex align-items-center">
          <input
            title="Quantity Used"
            type="number"
            id="energyInput"
            value={energyInputValue}
            onChange={handleInputChange}
            min="0"
            max={99999999.99}
            maxLength={10}
            onKeyDown={handleKeyDown}
            className="calculator-input"
          />
          {showSubModuleButton && (
            <>
              <button
                title="Details"
                type="button"
                className="gi-icon"
                onClick={() => setShowSubTypeModal(true)}
                id="editIcon_consumption"
              >
                <FontAwesomeIcon icon="edit" />
              </button>
              <UncontrolledTooltip
                className="tooltip-toolkit"
                placement="bottom"
                target="editIcon_consumption"
                modifiers={tooltipPreventOverflowModifiers}
              >
                {t('Select Construction Typology')}
              </UncontrolledTooltip>
            </>
          )}
        </div>

        <div className="col-sm-3">
          <Button
            htmlId="addRowEnergy"
            className="btn btn-primary px-4 py-1 calculator-add-button"
            style={{ float: 'right' }}
            text={t('Add Row')}
            onClick={handleAddRow}
            disabled={isAddButtonDisabled}
          />
        </div>
      </div>

      <div className="table-wrapper-calc-grid">
        <Table responsive className="table--bordered action-padding-calculator">
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Energy Type</th>
              <th>Quantity Used</th>
              <th className="text-right">Subtotal</th>
              <th className="action-column text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id}>
                <td>{row.energyType}</td>
                <td>
                  <NumberFormatterSinSeparador value={row.value} /> {row.energyUnit}{' '}
                </td>
                <td className="text-right">
                  <NumberFormatterSinSeparador value={row.totalEmission} /> t CO<sub>2</sub>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`remove-row-energy-${index}`}
                    className="btn-tertiary no-padding  action-calculator"
                    icon="trash-alt"
                    text=""
                    onClick={() => handleRemoveRow(index)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td className="subtotal-column-calculator" colSpan={3}>
                  <div>
                    Subtotal: <NumberFormatterSinSeparador value={totalCarbonSum} /> t CO
                    <sub>2</sub>
                  </div>
                </td>
                <td className="action-column text-right">
                  <Button
                    htmlId={`tr-landUselink-detail-${energyConsumption.moduleId}`}
                    className="btn-tertiary no-padding action-calculator"
                    icon="clipboard-list"
                    text=""
                    onClick={() => setShowDetailsModal(true)}
                  />

                  <UncontrolledTooltip
                    className="tooltip-toolkit"
                    placement="bottom"
                    target={`tr-landUselink-detail-${energyConsumption.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Details')}
                  </UncontrolledTooltip>
                </td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
      {showConfirmDelete && rowForDelete !== null && (
        <ConfirmModal
          visible={showConfirmDelete}
          buttonText={t('Confirm')}
          setVisible={setShowConfirmDelete}
          action={confirmRemoveRow}
        >
          {t('confirmDeleteCalculateMessage')}
        </ConfirmModal>
      )}
      <>
        <Modal
          title={energyConsumption.name}
          visible={showDetailsModal}
          setVisible={() => setShowDetailsModal(false)}
          actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
          customClass="extra_wide-modal calculator"
          onClosed={() => setShowDetailsModal(false)}
        >
          <div className="mb-0 table-responsive">
            <EnergyConsumptionDetails energyConsumption={energyConsumption} />
          </div>
        </Modal>

        <Modal
          title="Electricity"
          visible={showSubTypeModal}
          setVisible={() => setShowSubTypeModal(false)}
          actions={[
            { text: 'Cancel', action: () => setShowSubTypeModal(false) },
            {
              text: t('Add Electricity Info'),
              action: () => handleSubAddRow(),
              className: 'btn-primary',
              disabled: !enableAddElectricity,
            },
          ]}
          customClass="extra_wide-modal-x2"
          onClosed={() => setShowSubTypeModal(false)}
        >
          <div className="mb-0 table-responsive subModuleModal">
            <EnergyConsumptionElectricity
              energyConsumption={energyConsumption}
              setEnergyConsumption={(updatedEnergyConsumptionConstruction) =>
                setEnergy(updatedEnergyConsumptionConstruction)
              }
            />
          </div>
        </Modal>
      </>
    </>
  );
};

export default EnergyConsumptionSection;
